import React, { useState } from "react";
import PropTypes from "prop-types";
import { FormGroup, Input, Label, Button, ModalFooter, Spinner } from "reactstrap";
import { useSelector } from "react-redux";
import { selectAccount } from "../../../account/account-selectors";

const propTypes = {
  isDisableRenewal: PropTypes.bool.isRequired,
  zipSpots: PropTypes.arrayOf(
    PropTypes.shape({
      postal_code: PropTypes.string,
      spot_type: PropTypes.string,
      spots: PropTypes.number
    })
  ),
  isLoading: PropTypes.bool.isRequired,
  error: PropTypes.arrayOf(PropTypes.string),
  onSubmit: PropTypes.func.isRequired,
  onKeep: PropTypes.func.isRequired
};

const ReleaseZipCodesStep = ({
  isDisableRenewal,
  zipSpots = [],
  isLoading,
  error = null,
  onSubmit,
  onKeep
}) => {
  const account = useSelector(selectAccount);
  const [fullName, setFullName] = useState("");
  const [agentRelationship, setAgentRelationship] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [localErrors, setLocalErrors] = useState([]);

  const handleSubmit = () => {
    const errs = [];
    if (!fullName.trim()) errs.push("Please enter your full name.");
    if (!agentRelationship || agentRelationship === "select")
      errs.push("Please select your relationship to the customer being advertised.");
    if (!termsAccepted) errs.push("You must accept the release agreement.");
    if (errs.length) {
      setLocalErrors(errs);
      return;
    }
    setLocalErrors([]);
    onSubmit({ fullName: fullName.trim(), agentRelationship });
  };

  const allErrors = [...(error || []), ...localErrors];

  return (
    <div>
      <p className="text-center text-muted mb-3">Agreement to Release Zip Codes</p>
      <p className="mb-3">
        In order to {isDisableRenewal ? "cancel the renewal of" : "cancel"} your Adwerx campaign, we
        need your explicit permission to release the zip codes that currently belong to you, so that
        we can make them available to other potential customers.
      </p>

      <div className="background-light-blue mb-3 p-4">
        <p className="font-weight-bold mb-2">
          By signing the document below, you acknowledge that you understand the following:
        </p>
        <div className="mb-0">
          <p className="mb-2">
            1. You are canceling your claim on the following zip codes in the AdWerx system:
            {zipSpots.length > 0 && (
              <span className="d-block mt-2">
                {zipSpots.map(z => (
                  <span
                    key={`${z.postal_code}-${z.spot_type}`}
                    className="badge badge-primary mr-1 mb-1"
                  >
                    {z.spots}{" "}
                    {z.spot_type
                      ? `${z.spot_type} spot${z.spots !== 1 ? "s" : ""}`
                      : `spot${z.spots !== 1 ? "s" : ""}`}{" "}
                    for {z.postal_code}
                  </span>
                ))}
              </span>
            )}
          </p>
          {isDisableRenewal ? (
            <>
              <p className="mb-2">
                2. AdWerx will make these zip codes{" "}
                <strong>available to all other local customers</strong> when your campaign ends.
              </p>
              <p className="mb-2">
                3. You forfeit your current locked-in price for your zip codes and acknowledge that
                AdWerx may increase prices at any point in the future.
              </p>
              <p className="mb-0">
                4. Your zip codes may be sold out in the future if you decide to repurchase them.
              </p>
            </>
          ) : (
            <>
              <p className="mb-2">
                2. AdWerx will immediately make these zip codes{" "}
                <strong>available to all other local potential customers</strong>.
              </p>
              <p className="mb-0">
                3. AdWerx may immediately contact other local potential customers to sell these zip{" "}
                codes to them.
              </p>
            </>
          )}
        </div>
      </div>

      {allErrors.length > 0 && (
        <div className="notifications danger mb-3">
          {allErrors.map(e => (
            <div key={e} className="notifications__error-message">
              {e}
            </div>
          ))}
        </div>
      )}

      <p className="font-weight-bold mb-1">
        Authorization to release Zip Codes for: {account.owningUser.firstName}{" "}
        {account.owningUser.lastName}
      </p>
      <FormGroup>
        <Label for="release_full_name">Full Name</Label>
        <Input
          id="release_full_name"
          type="text"
          placeholder="i.e. John Doe"
          value={fullName}
          onChange={e => setFullName(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <Label for="release_relationship">Relationship to Customer Being Advertised</Label>
        <Input
          id="release_relationship"
          type="select"
          value={agentRelationship}
          onChange={e => setAgentRelationship(e.target.value)}
        >
          <option value="select">-Select One-</option>
          <option value="agent">I am the customer</option>
          <option value="authorized">I&apos;ve been authorized by the customer</option>
        </Input>
      </FormGroup>

      <FormGroup check className="mb-3">
        <Label check>
          <Input
            type="checkbox"
            checked={termsAccepted}
            onChange={e => setTermsAccepted(e.target.checked)}
          />{" "}
          I understand and acknowledge the above
        </Label>
      </FormGroup>

      <ModalFooter className="pt-2 px-0">
        <Button color="secondary" className="btn-sharp" onClick={onKeep}>
          Keep My Zip Codes
        </Button>
        <Button color="danger" className="btn-sharp" onClick={handleSubmit} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner color="light" size="sm" /> Processing...
            </>
          ) : (
            "Release My Zip Codes"
          )}
        </Button>
      </ModalFooter>
    </div>
  );
};

ReleaseZipCodesStep.propTypes = propTypes;

export default ReleaseZipCodesStep;
